import { useEffect, useRef, useState } from 'react';
import { Icon } from '../ui/Icon.tsx';
import { Sheet } from '../ui/Sheet.tsx';
import type { MenuEntry, MenuItem } from './ContextMenu.tsx';

// Actievel op de telefoon, de tegenhanger van het rechtsklikmenu: dezelfde items (mailMenuItems) als
// rijen van 48 px in een vel. Een item met kinderen (Verplaatsen naar…) opent een tweede niveau in
// hetzelfde vel, met Terug naar het eerste niveau. Een keuze voert het item uit en sluit het vel.
// Het tweede niveau krijgt bewust geen eigen laag (useLayer): de terugbeweging sluit het hele vel.

export function ActionSheet(props: { items: MenuEntry[]; onClose: () => void; title?: string | undefined }) {
  const [openId, setOpenId] = useState<string | null>(null);
  const sub = openId ? props.items.find((e): e is MenuItem => e !== 'separator' && e.id === openId) : undefined;
  const title = props.title ?? 'Acties';
  const box = useRef<HTMLDivElement>(null);
  // Een ander niveau begint bovenaan, ook als het vorige gescrold was (het vel scrolt, niet deze lijst).
  useEffect(() => {
    const scroller = box.current?.parentElement;
    if (scroller) scroller.scrollTop = 0;
  }, [openId]);

  const choose = (it: MenuItem) => {
    if (it.disabled) return;
    if (it.children) {
      setOpenId(it.id);
      return;
    }
    it.onSelect?.();
    props.onClose();
  };

  const row = (it: MenuItem) => (
    <button
      key={it.id}
      type="button"
      role="menuitem"
      disabled={it.disabled}
      aria-haspopup={it.children ? 'menu' : undefined}
      onClick={() => choose(it)}
      className={`w-full h-12 px-4 flex items-center gap-3 text-left text-[16px] disabled:opacity-40 ${it.danger ? 'text-red active:bg-red-soft' : 'text-ink active:bg-tint'}`}
    >
      {it.icon ? <Icon name={it.icon} size={20} className={`shrink-0 ${it.danger ? 'text-red' : 'text-ink-soft'}`} /> : <span className="w-5 shrink-0" />}
      <span className="flex-1 min-w-0 truncate">{it.label}</span>
      {it.children ? <Icon name="chevron" size={18} className="shrink-0 text-muted" /> : null}
    </button>
  );

  return (
    <Sheet title={title} onClose={props.onClose} height="auto">
      <div ref={box} className="pb-2">
        {sub ? (
          <>
            <div className="sticky top-0 z-10 h-12 flex items-center gap-2 pl-2 pr-4 bg-surface border-b border-line">
              <button type="button" aria-label="Terug" onClick={() => setOpenId(null)} className="h-11 pl-1 pr-3 shrink-0 inline-flex items-center gap-0.5 rounded-md text-[15px] font-medium text-accent active:bg-tint">
                <Icon name="chevron" size={20} className="rotate-180" /> Terug
              </button>
              <span className="flex-1 min-w-0 truncate font-semibold text-ink">{sub.label}</span>
            </div>
            <div role="menu" aria-label={sub.label} className="py-1">
              {(sub.children ?? []).map(row)}
            </div>
          </>
        ) : (
          <div role="menu" aria-label={title} className="py-1">
            {props.items.map((e, i) => (e === 'separator' ? <div key={`sep-${i}`} role="separator" className="border-t border-line my-1" /> : row(e)))}
          </div>
        )}
      </div>
    </Sheet>
  );
}
